'use client';
import { jsx } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import { createContext, forwardRef, useState, useRef, useEffect } from 'react';
import { warn } from '../index.js';
import { useMergeRefs } from '../hooks/use-merge-refs/use-merge-refs.js';
import '@digdir/designsystemet-web';

const RovingFocusContext = createContext({
    elements: { current: new Map() },
    getOrderedItems: () => [],
    setFocusableValue: () => {
        /* intentionally empty */
    },
    onShiftTab: () => {
        /* intentionally empty */
    },
    focusableValue: null,
    orientation: 'horizontal',
});
/** @deprecated RovingFocusRoot is deprecated. */
const RovingFocusRoot = forwardRef(({ activeValue, asChild, orientation = 'horizontal', onBlur, onFocus, ...rest }, ref) => {
    const Component = asChild ? Slot : 'div';
    const activeValueOrNull = activeValue ?? null;
    const [focusableValue, setFocusableValue] = useState(activeValueOrNull);
    const [isShiftTabbing, setIsShiftTabbing] = useState(false);
    const elements = useRef(new Map());
    const myRef = useRef(null);
    const refs = useMergeRefs([ref, myRef]);
    const getOrderedItems = () => {
        if (!myRef.current)
            return [];
        const elementsFromDOM = Array.from(myRef.current.querySelectorAll('[data-roving-tabindex-item]'));
        return Array.from(elements.current)
            .sort((a, b) => elementsFromDOM.indexOf(a[1]) - elementsFromDOM.indexOf(b[1]))
            .map(([value, element]) => ({ value, element }));
    };
    useEffect(() => {
        setFocusableValue(activeValueOrNull);
    }, [activeValueOrNull]);
    warn('RovingFocusRoot is deprecated.');
    return (jsx(RovingFocusContext.Provider, { value: {
            elements,
            getOrderedItems,
            focusableValue,
            setFocusableValue,
            onShiftTab: () => {
                setIsShiftTabbing(true);
            },
            orientation,
        }, children: jsx(Component, { ...rest, tabIndex: isShiftTabbing ? -1 : 0, onBlur: (e) => {
                onBlur?.(e);
                setIsShiftTabbing(false);
                setFocusableValue(activeValue ?? null);
            }, onFocus: (e) => {
                onFocus?.(e);
                if (e.target !== e.currentTarget)
                    return;
                const orderedItems = getOrderedItems();
                if (orderedItems.length === 0)
                    return;
                if (focusableValue != null) {
                    elements.current.get(focusableValue)?.focus();
                }
                else if (activeValue != null) {
                    elements.current.get(activeValue)?.focus();
                }
                else {
                    orderedItems.at(0)?.element.focus();
                }
            }, ref: refs }) }));
});

export { RovingFocusContext, RovingFocusRoot };
